import React, { useEffect, useMemo, useState } from "react";
import { usageService } from "../services/usage.service";
import { UsageSummary } from "../types";

const Usage: React.FC = () => {
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSummary = async () => {
      try {
        const data = await usageService.getSummary();
        setSummary(data);
      } catch (err: any) {
        setError(err.response?.data?.message || "Failed to load usage");
      } finally {
        setLoading(false);
      }
    };

    loadSummary();
  }, []);

  const deviceHealth = useMemo(() => {
    if (!summary) {
      return { ratio: 0, label: "No installs yet" };
    }
    const ratio =
      summary.machine.totalInstallations > 0
        ? summary.machine.activeInstallations /
          summary.machine.totalInstallations
        : 0;
    const label =
      ratio > 0.75
        ? "Devices look healthy"
        : ratio > 0.4
        ? "Moderate activity"
        : "Low device activity";
    return { ratio, label };
  }, [summary]);

  const tasksLinePoints = useMemo(() => {
    if (!summary) return "";
    const values = summary.chat.tasksByDay.map((item) => item.count);
    const max = Math.max(...values, 1);
    return values
      .map((value, index) => {
        const x = (index / Math.max(values.length - 1, 1)) * 100;
        const y = 95 - (value / max) * 70;
        return `${x},${y}`;
      })
      .join(" ");
  }, [summary]);

  const tasksAreaPoints = useMemo(() => {
    if (!summary || !tasksLinePoints) return "";
    return `0,100 ${tasksLinePoints} 100,100`;
  }, [summary, tasksLinePoints]);

  const tokensMax = useMemo(() => {
    if (!summary) return 1;
    return Math.max(
      ...summary.chat.tokensByDay.map(
        (entry) => entry.tokensIn + entry.tokensOut
      ),
      1
    );
  }, [summary]);

  const topInsights = useMemo(() => {
    if (!summary) {
      return [];
    }
    const topPlatform = summary.machine.byPlatform[0]?.label || "Unknown";
    const topOs = summary.machine.byOs[0]?.label || "Unknown";
    const topModel = summary.chat.topModels[0]?.label || "N/A";
    const totalTokens =
      summary.chat.totalTokensIn + summary.chat.totalTokensOut;
    return [
      `Most activity is on ${topPlatform} running ${topOs}.`,
      `Your busiest model is ${topModel}.`,
      `You have processed ${totalTokens.toLocaleString()} tokens so far.`,
    ];
  }, [summary]);

  if (loading) {
    return <div>Loading usage dashboard...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  if (!summary) {
    return <div>No usage data yet.</div>;
  }

  return (
    <div className="usage-page">
      <style>
        {`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');

        .usage-page {
          --ink: #0b1220;
          --muted: #5d6b7c;
          --accent: #f07c2a;
          --accent-cool: #12b5a6;
          --accent-soft: rgba(240, 124, 42, 0.14);
          --panel: #ffffff;
          --stroke: rgba(11, 18, 32, 0.08);
          --bg: #f5efe6;
          font-family: 'Sora', 'Segoe UI', sans-serif;
          color: var(--ink);
          position: relative;
          border-radius: 30px;
          padding: 32px;
          min-height: calc(100vh - 120px);
          background:
            radial-gradient(circle at 10% 10%, rgba(18, 181, 166, 0.2), transparent 45%),
            radial-gradient(circle at 90% 15%, rgba(240, 124, 42, 0.18), transparent 50%),
            var(--bg);
          overflow: hidden;
        }

        .usage-orb {
          position: absolute;
          border-radius: 999px;
          filter: blur(0.5px);
          opacity: 0.5;
          pointer-events: none;
        }

        .usage-orb.one {
          width: 220px;
          height: 220px;
          background: rgba(18, 181, 166, 0.35);
          top: -80px;
          right: -40px;
        }

        .usage-orb.two {
          width: 160px;
          height: 160px;
          background: rgba(240, 124, 42, 0.25);
          bottom: 40px;
          left: -50px;
        }

        .usage-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          gap: 24px;
          margin-bottom: 24px;
          position: relative;
          z-index: 1;
        }

        .usage-title {
          font-size: clamp(2rem, 4vw, 2.9rem);
          margin: 0 0 8px;
        }

        .usage-subtitle {
          color: var(--muted);
          margin: 0;
          font-size: 1rem;
        }

        .usage-summary {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
          position: relative;
          z-index: 1;
        }

        .usage-card {
          background: var(--panel);
          border-radius: 20px;
          padding: 18px;
          border: 1px solid var(--stroke);
          box-shadow: 0 18px 32px rgba(11, 18, 32, 0.08);
          animation: rise 0.6s ease forwards;
        }

        .usage-card h4 {
          margin: 0 0 8px;
          font-size: 0.95rem;
          color: var(--muted);
          font-weight: 600;
        }

        .usage-card strong {
          font-size: 1.5rem;
        }

        .usage-grid {
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          gap: 18px;
          position: relative;
          z-index: 1;
        }

        .usage-panel {
          background: var(--panel);
          border-radius: 24px;
          padding: 20px;
          border: 1px solid var(--stroke);
          box-shadow: 0 24px 40px rgba(11, 18, 32, 0.1);
          animation: fadeUp 0.6s ease forwards;
        }

        .usage-panel h3 {
          margin: 0 0 12px;
          font-size: 1.15rem;
        }

        .usage-panel p {
          color: var(--muted);
          margin-top: 0;
        }

        .panel-wide {
          grid-column: span 7;
        }

        .panel-tall {
          grid-column: span 5;
        }

        .panel-full {
          grid-column: 1 / -1;
        }

        .line-chart {
          width: 100%;
          height: 140px;
        }

        .line-chart path {
          fill: none;
          stroke: var(--accent);
          stroke-width: 3;
        }

        .line-chart .area {
          fill: rgba(240, 124, 42, 0.15);
          stroke: none;
        }

        .chart-caption {
          display: flex;
          justify-content: space-between;
          font-size: 0.8rem;
          color: var(--muted);
          margin-top: 8px;
        }

        .token-bars {
          display: grid;
          grid-template-columns: repeat(${summary.chat.tokensByDay.length}, 1fr);
          gap: 6px;
          align-items: flex-end;
          height: 140px;
          margin-top: 14px;
        }

        .token-bar {
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          gap: 4px;
          animation: grow 0.8s ease forwards;
        }

        .token-bar span {
          width: 100%;
          border-radius: 6px;
        }

        .token-in {
          background: rgba(18, 181, 166, 0.8);
        }

        .token-out {
          background: rgba(240, 124, 42, 0.85);
        }

        .donut {
          width: 150px;
          height: 150px;
          border-radius: 50%;
          background: conic-gradient(
            var(--accent-cool) ${deviceHealth.ratio * 360}deg,
            rgba(11, 18, 32, 0.08) 0deg
          );
          display: grid;
          place-items: center;
          margin: 10px auto;
          position: relative;
        }

        .donut::after {
          content: "";
          width: 105px;
          height: 105px;
          border-radius: 50%;
          background: #fff;
          position: absolute;
        }

        .donut-label {
          position: relative;
          font-weight: 600;
          z-index: 1;
        }

        .pill-list {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 12px;
        }

        .pill {
          background: rgba(11, 18, 32, 0.06);
          padding: 6px 12px;
          border-radius: 999px;
          font-size: 0.85rem;
        }

        .bar-list {
          display: grid;
          gap: 10px;
          margin-top: 10px;
        }

        .bar-row {
          display: grid;
          grid-template-columns: 120px 1fr auto;
          gap: 10px;
          align-items: center;
        }

        .bar-track {
          height: 10px;
          background: rgba(11, 18, 32, 0.08);
          border-radius: 999px;
          overflow: hidden;
        }

        .bar-fill {
          height: 100%;
          background: linear-gradient(120deg, var(--accent), var(--accent-cool));
          border-radius: 999px;
          animation: widen 0.9s ease forwards;
        }

        .recent-list {
          display: grid;
          gap: 12px;
        }

        .recent-item {
          display: flex;
          justify-content: space-between;
          padding: 10px 12px;
          border-radius: 14px;
          background: rgba(11, 18, 32, 0.04);
          font-size: 0.9rem;
        }

        .insight-grid {
          display: grid;
          gap: 10px;
          margin-top: 12px;
        }

        .insight-card {
          padding: 12px 14px;
          border-radius: 14px;
          background: rgba(18, 181, 166, 0.1);
          border: 1px solid rgba(18, 181, 166, 0.25);
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes rise {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes grow {
          from { opacity: 0; transform: scaleY(0.6); }
          to { opacity: 1; transform: scaleY(1); }
        }

        @keyframes widen {
          from { width: 0; }
          to { width: 100%; }
        }

        @media (max-width: 900px) {
          .panel-wide, .panel-tall {
            grid-column: 1 / -1;
          }
          .bar-row {
            grid-template-columns: 1fr;
            gap: 6px;
          }
        }
        `}
      </style>

      <div className="usage-orb one" />
      <div className="usage-orb two" />

      <header className="usage-header">
        <div>
          <h1 className="usage-title">Usage dashboard</h1>
          <p className="usage-subtitle">
            Live signals from machine installs, chats, and model usage.
          </p>
        </div>
      </header>

      <section className="usage-summary">
        <div className="usage-card">
          <h4>Total devices</h4>
          <strong>{summary.machine.totalInstallations}</strong>
        </div>
        <div className="usage-card">
          <h4>Active devices (30d)</h4>
          <strong>{summary.machine.activeInstallations}</strong>
        </div>
        <div className="usage-card">
          <h4>Total tasks</h4>
          <strong>{summary.chat.totalTasks}</strong>
        </div>
        <div className="usage-card">
          <h4>Total tokens</h4>
          <strong>
            {(summary.chat.totalTokensIn + summary.chat.totalTokensOut).toLocaleString()}
          </strong>
        </div>
      </section>

      <section className="usage-grid">
        <div className="usage-panel panel-wide">
          <h3>Tasks over time</h3>
          <svg viewBox="0 0 100 100" className="line-chart" preserveAspectRatio="none">
            <polygon className="area" points={tasksAreaPoints} />
            <polyline points={tasksLinePoints} />
          </svg>
          <div className="chart-caption">
            <span>Last {summary.chat.tasksByDay.length} days</span>
            <span>{summary.chat.activeTasks} active tasks</span>
          </div>
        </div>

        <div className="usage-panel panel-tall">
          <h3>Device health</h3>
          <div className="donut">
            <span className="donut-label">
              {Math.round(deviceHealth.ratio * 100)}%
            </span>
          </div>
          <p>{deviceHealth.label}</p>
          <div className="pill-list">
            {summary.machine.byPlatform.slice(0, 4).map((item) => (
              <span className="pill" key={item.label}>
                {item.label} · {item.count}
              </span>
            ))}
          </div>
        </div>

        <div className="usage-panel panel-wide">
          <h3>Token flow</h3>
          <p>Inbound vs outbound tokens over the last two weeks.</p>
          <div className="token-bars">
            {summary.chat.tokensByDay.map((entry) => {
              const total = entry.tokensIn + entry.tokensOut;
              const height = (total / tokensMax) * 100;
              const inHeight = total > 0 ? (entry.tokensIn / total) * height : 0;
              const outHeight = total > 0 ? (entry.tokensOut / total) * height : 0;
              return (
                <div className="token-bar" key={entry.date} style={{ height: `${height}%` }}>
                  <span className="token-in" style={{ height: `${inHeight}%` }} />
                  <span className="token-out" style={{ height: `${outHeight}%` }} />
                </div>
              );
            })}
          </div>
        </div>

        <div className="usage-panel panel-tall">
          <h3>Top models</h3>
          <div className="bar-list">
            {summary.chat.topModels.map((model) => {
              const max = summary.chat.topModels[0]?.count || 1;
              const width = (model.count / max) * 100;
              return (
                <div className="bar-row" key={model.label}>
                  <span>{model.label}</span>
                  <div className="bar-track">
                    <div className="bar-fill" style={{ width: `${width}%` }} />
                  </div>
                  <strong>{model.count}</strong>
                </div>
              );
            })}
          </div>
        </div>

        <div className="usage-panel panel-tall">
          <h3>Environment mix</h3>
          <div className="bar-list">
            {summary.chat.environmentOs.map((env) => {
              const max = summary.chat.environmentOs[0]?.count || 1;
              const width = (env.count / max) * 100;
              return (
                <div className="bar-row" key={env.label}>
                  <span>{env.label}</span>
                  <div className="bar-track">
                    <div className="bar-fill" style={{ width: `${width}%` }} />
                  </div>
                  <strong>{env.count}</strong>
                </div>
              );
            })}
          </div>
        </div>

        <div className="usage-panel panel-wide">
          <h3>Recent devices</h3>
          <div className="recent-list">
            {summary.machine.recentInstallations.map((inst) => (
              <div className="recent-item" key={inst.machineHostname + String(inst.lastSeenAt)}>
                <span>
                  {inst.machineHostname} · {inst.machineOs}
                </span>
                <span>
                  {inst.vscodePlatform} · {inst.extensionVersion}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="usage-panel panel-full">
          <h3>Insights</h3>
          <div className="insight-grid">
            {topInsights.map((insight) => (
              <div className="insight-card" key={insight}>
                {insight}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Usage;
